import { useEffect, useRef, useState } from "react"
import { useIntl } from "react-intl"

import classNames from "classnames"
import dayjs from "dayjs"

import { Button, Dialog, ICONS, StaffBodyTextS, StaffBodyTextXS, StaffHeading3, TextInput } from "@doktor-se/bones-ui"
import { ConfirmedPopover } from "@doktor-se/bones-ui"
import { ConfirmedPopoverRef } from "@doktor-se/bones-ui/dist/components/ConfirmedPopover/ConfirmedPopover"
import { useTimeToNow } from "@doktor-se/bones-ui/dist/web-shared/hooks"
import { Staff } from "@doktor-se/bones-ui/dist/web-shared/types"

import { updateBookingNote } from "api"
import { useAppDispatch } from "lib/hooks"
import { BookedAppointment } from "reducers/conversations/conversations.reducer"

import styles from "./Cells.module.scss"

interface NameCellProps {
  name: string
  isChild: boolean
}

export const NameCell = ({ name, isChild }: NameCellProps) => (
  <div className={styles.nameCell}>
    {isChild && <ICONS.Child />}
    {name}
  </div>
)

interface DateTimePillCellProps {
  value: string | number
  postponed?: boolean
}

export const DateTimePillCell = ({ value, postponed = false }: DateTimePillCellProps) => {
  const intl = useIntl()
  const time = useTimeToNow(value)
  if (postponed) {
    const postponedUntil = () => {
      return dayjs(value).calendar(undefined, {
        sameDay: `HH:mm [${intl.formatMessage({ id: "unassign.postpone.today" })}]`,
        nextDay: `HH:mm [${intl.formatMessage({ id: "unassign.postpone.tomorrow" })}]`
      })
    }
    return (
      <div className={styles.dateCell}>
        <StaffBodyTextXS>{postponedUntil()}</StaffBodyTextXS>
      </div>
    )
  }

  return (
    <div className={styles.dateCell}>
      <StaffBodyTextXS>
        {time.date
          ? intl.formatDate(time.date, {
              day: "2-digit",
              month: "2-digit",
              year: "numeric",
              hour: "2-digit",
              minute: "2-digit"
            })
          : time.timeLeft}
      </StaffBodyTextXS>
    </div>
  )
}

interface DateCellProps {
  value: string
  past?: boolean
}

export const DateCell = ({ value, past = false }: DateCellProps) => {
  const intl = useIntl()
  return (
    <div className={classNames(styles.dateCell, { [styles.pastDateCell]: past })}>
      <StaffBodyTextXS>
        {intl.formatDate(value, {
          day: "2-digit",
          month: "2-digit",
          year: "numeric",
          hour: "2-digit",
          minute: "2-digit"
        })}
      </StaffBodyTextXS>
    </div>
  )
}
export const StatusCell = ({ status }: { status: string }) => {
  const intl = useIntl()
  const label =
    status === "opened"
      ? "label.open"
      : status === "closed"
        ? "label.closed"
        : status === "booked"
          ? "label.booked"
          : status
  return (
    <div
      className={classNames(styles.statusCell, {
        [styles.booked]: status === "booked",
        [styles.closed]: status === "closed",
        [styles.opened]: status === "opened"
      })}>
      {intl.formatMessage({ id: label })}
    </div>
  )
}

interface BooleanCellProps {
  value: boolean
}

export const BooleanCell = ({ value }: BooleanCellProps) => {
  const intl = useIntl()
  const label = value ? "label.yes" : "label.no"
  return (
    <div className={classNames(styles.booleanCell, { [styles.true]: value, [styles.false]: !value })}>
      {intl.formatMessage({ id: label })}
    </div>
  )
}

interface AssignedStaffCellProps {
  staff: Staff[]
  staffId?: string | null
}

const getInitials = (displayName: string): string => {
  const cleanedName = displayName.replace(/[()]/g, "").trim()
  const parts = cleanedName.split(/\s+/).filter(part => part.length > 0)

  if (parts.length >= 2) {
    return (parts[0][0] + parts[1][0]).toUpperCase()
  }

  return (parts[0] || "").substring(0, 2).toUpperCase().padEnd(2, "X")
}

const Tooltip = ({ children, style }: { children: string; style?: string }) => (
  <div className={style || styles.tooltip}>
    <StaffBodyTextXS>{children}</StaffBodyTextXS>
  </div>
)

export const AssignedStaffCell = ({ staff, staffId }: AssignedStaffCellProps) => {
  const intl = useIntl()
  const [showTooltip, setShowTooltip] = useState(false)
  const delayTimeout = useRef<NodeJS.Timeout | null>(null)
  const handleMouseEnter = () => {
    delayTimeout.current = setTimeout(() => {
      setShowTooltip(true)
    }, 500)
  }

  const handleMouseLeave = () => {
    if (delayTimeout.current) {
      clearTimeout(delayTimeout.current)
    }
    setShowTooltip(false)
  }

  if (staffId) {
    const staffName = staff.find(s => s.id === staffId)?.displayName
    if (staffName) {
      const initials = getInitials(staffName)
      return (
        <div className={styles.assignedCell} onMouseEnter={handleMouseEnter} onMouseLeave={handleMouseLeave}>
          {showTooltip && <Tooltip>{staffName}</Tooltip>}
          <StaffBodyTextXS>{initials}</StaffBodyTextXS>
        </div>
      )
    }
  }

  return (
    <div>
      <StaffBodyTextXS>{intl.formatMessage({ id: "label.overview.not_assigned" })}</StaffBodyTextXS>
    </div>
  )
}

export const NoteTextCell = ({ conversation }: { conversation: BookedAppointment }) => {
  const [showTooltip, setShowTooltip] = useState(false)
  const delayTimeout = useRef<NodeJS.Timeout | null>(null)
  const [value, setValue] = useState(conversation.bookingNote || "")
  const [isSaving, setIsSaving] = useState(false)
  const bookingNoteRef = useRef(conversation.bookingNote)
  const dispatch = useAppDispatch()
  const inputWrapperRef = useRef<HTMLDivElement>(null)
  const savedPopoverRef = useRef<ConfirmedPopoverRef>(null)
  const intl = useIntl()

  const [externalEditDialogIsOpen, setExternalEditDialogIsOpen] = useState(false)

  useEffect(() => {
    const isInputFocused = inputWrapperRef.current?.querySelector("input") === document.activeElement
    if (isInputFocused && bookingNoteRef.current !== conversation.bookingNote) {
      // Focused and comment field has been edited
      setExternalEditDialogIsOpen(true)
      return
    }

    if (!isInputFocused && bookingNoteRef.current !== conversation.bookingNote) {
      // Unfocus, just update.
      setValue(conversation.bookingNote || "")
      bookingNoteRef.current = conversation.bookingNote
      return
    }
  }, [conversation.bookingNote, inputWrapperRef])

  // Mouse enters input field
  const inputWrapperHandleMouseEnter = () => {
    delayTimeout.current = setTimeout(() => {
      setShowTooltip(true)
    }, 500)
  }

  // Mouse leaves input field
  const inputWrapperHandleMouseLeave = () => {
    if (delayTimeout.current) {
      clearTimeout(delayTimeout.current)
    }
    setShowTooltip(false)
  }

  const handleClick = (e: React.MouseEvent) => {
    e.stopPropagation()
  }

  const inputHandleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === "Enter") {
      inputHandleSave()
    }
  }

  const inputHandleSave = async () => {
    setIsSaving(true)

    if (value === conversation.bookingNote) {
      setIsSaving(false)
      return
    }

    try {
      bookingNoteRef.current = value
      await dispatch(updateBookingNote(conversation.bookingId, value))
      savedPopoverRef.current?.show()
    } catch (err) {
      setValue(conversation.bookingNote) // Reset on error
    } finally {
      setIsSaving(false)
    }
  }

  const inputWrapperHandleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === "Enter" || e.key === " ") {
      e.preventDefault()

      const input = e.currentTarget.querySelector("input")
      input?.focus()
    }
  }

  const dialogHandleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === "Enter" || e.key === " ") {
      e.preventDefault()
      inputHandleSave()
    }
  }

  const dialogCancelEdit = () => {
    bookingNoteRef.current = conversation.bookingNote
    setValue(bookingNoteRef.current || "")
    setExternalEditDialogIsOpen(false)
  }

  const dialogContinueEdit = () => {
    bookingNoteRef.current = conversation.bookingNote
    setExternalEditDialogIsOpen(false)
  }

  return (
    <div>
      <div
        className={styles.textCell}
        onClick={handleClick}
        role="button"
        tabIndex={0}
        onKeyDown={inputWrapperHandleKeyDown}
        onMouseEnter={inputWrapperHandleMouseEnter}
        onMouseLeave={inputWrapperHandleMouseLeave}
        ref={inputWrapperRef}
        aria-label="Edit booking note">
        {showTooltip && value && <Tooltip style={styles.tooltipNote}>{value}</Tooltip>}
        <TextInput
          variant="onSurface"
          label=""
          aria-labelledby="Comment input"
          isDisabled={isSaving}
          fullWidth
          autoComplete="off"
          data-lpignore="true"
          onChange={(v: string) => setValue(v)}
          onKeyDown={inputHandleKeyDown}
          value={value}
        />
        {(bookingNoteRef.current || "") !== value && (
          <div
            className={styles.saveIcon}
            onClick={inputHandleSave}
            role="button"
            tabIndex={0}
            onKeyDown={e => {
              if (e.key === "Enter" || e.key === " ") {
                inputHandleSave()
              }
            }}
            aria-label="Save note button">
            <ICONS.Check />
          </div>
        )}
        <ConfirmedPopover
          confirmationText={intl.formatMessage({ id: "label.saved" })}
          timeoutMs={2000}
          triggerRef={inputWrapperRef}
          ref={savedPopoverRef}
          placement={"right"}
        />
      </div>
      <div onKeyDown={dialogHandleKeyDown} role="button" tabIndex={0} onClick={e => e.stopPropagation()}>
        <Dialog
          isOpen={externalEditDialogIsOpen}
          closeAriaLabel="close"
          fixedWidth={true}
          onClose={() => setExternalEditDialogIsOpen(false)}>
          <div className={styles.dialogContainer}>
            <div>
              <ICONS.Warning className={styles.dialogWarningIcon} />
            </div>
            <StaffHeading3>{intl.formatMessage({ id: "overview.comment.external.edit.dialog.title" })}</StaffHeading3>
            <StaffBodyTextS className={styles.externalEditText}>{conversation.bookingNote}</StaffBodyTextS>
            <StaffBodyTextS>
              {intl.formatMessage({ id: "overview.comment.external.edit.dialog.prompt" })}
            </StaffBodyTextS>

            <div className={styles.rowContainer}>
              <Button color="primary" variant="primary" onPress={dialogCancelEdit}>
                {intl.formatMessage({ id: "label.yes" })}
              </Button>
              <Button color="primaryLight" variant="primary" onPress={dialogContinueEdit}>
                {intl.formatMessage({ id: "label.no" })}
              </Button>
            </div>
          </div>
        </Dialog>
      </div>
    </div>
  )
}
